import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIGS
import { AppIntegrationAccountApiService } from '../../../shared';
import {
  EAppIntegrationAccountProductFilter,
  IAppIntegrationAccount,
  IAppIntegrationAccountProduct,
} from '../../../shared/interfaces/app-integration-account.interface';
import { trackByControlValue, trackById } from '../../../shared/util/track-by.util';
import { AppIntegrationAccountBulkCreateProductsModalComponent } from '../app-integration-account-bulk-create-products-modal/app-integration-account-bulk-create-products-modal.component';
import { AppIntegrationAccountLocationComponent } from '../app-integration-account-location/app-integration-account-location.component';
import { AppIntegrationAccountProductFilterFormComponent } from '../app-integration-account-product-filter-form/app-integration-account-product-filter-form.component';

import { ImportTypeConstant } from './../../../../../app/core/_constant/imports.constants';
import { ImporterService } from './../../../../../app/core/tools/services/importer.service';
import { EProductMappingType } from './../../../channel/interfaces/channel-product-mapping.interface';
import { DialogConfig } from './../../../shared/configs/dialog.config';
import { PaginationConfig } from './../../../shared/configs/paginator.config';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';

// COMPONENTS

// INTERFACES

import { IPagination } from './../../../shared/interfaces/paginator.interface';

// SERVICES
import { AppIntegrationAccountProductsApiService } from './../../../shared/services/api/app-integration/app-integration-account-products-api.service';
import { RfqApiService } from './../../../shared/services/api/rfq/rfq-api.service';

// UTILS

@Component({
  selector: 'lum-app-integration-account-manage-products',
  templateUrl: './app-integration-account-manage-products.component.html',
  styleUrls: ['./app-integration-account-manage-products.component.scss'],
})
export class AppIntegrationAccountManageProductsComponent implements OnInit {
  constructor(
    private dialog :MatDialog,
    private appIntegrationAccountApiService :AppIntegrationAccountApiService,
    private appIntegrationAccountProductsApiService :AppIntegrationAccountProductsApiService,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private rfqApiService :RfqApiService,
    private route :ActivatedRoute,
    private router :Router,
    private snackBar :MatSnackBar,
    private importerService :ImporterService,
  ) {}

  products :IAppIntegrationAccountProduct[] = [];
  appIntegrationAccount :IAppIntegrationAccount;
  loadingProducts :boolean = false;
  autoMapLoading :boolean = false;
  filteredRfqs :any[] = [];
  loadingRfqs :boolean = false;
  form :UntypedFormGroup;
  accountId :number;
  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    path: '',
    per_page: PaginationConfig.perPage,
    to: null,
    total: 0,
  };
  queryParams :{ [param :string] :string | string[] };
  eAppIntegrationAccountProductFilter = EAppIntegrationAccountProductFilter;
  eProductMappingType = EProductMappingType;

  isNotSupportedPullProducts :boolean = false;

  private onDestroy$ = new Subject();

  trackByControlValue = trackByControlValue;
  trackById = trackById;

  ngOnInit() :void {
    this.accountId = Number(this.route?.parent?.snapshot.paramMap.get('id'));

    if (!this.accountId) {
      this.router.navigate(['settings/app-integration-account-settings']);

      return;
    }

    this.initQueryParams();
    this.initForm([]);
    this.getAppIntegrationAccount();
    this.getProducts();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSearchRfqs(sku :string) :void {
    this.loadingRfqs = true;
    this.rfqApiService
      .searchSKU({
        sku,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingRfqs = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          this.filteredRfqs = response;
          if (response.length === 0) {
            this.snackBar.open('No RFQs found', 'Info', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to search RFQs', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onSelectRfq(event :MatAutocompleteSelectedEvent, index :number) :void {
    const lumSkuId = event.option.value.id;
    const data = {
      lumSkuId,
    };

    const productId = this.productsFormArray.at(index).get('id')?.value;

    this.updateProduct({ data, index, productId });
  }

  onClearRfq(index :number) :void {
    this.productsFormArray.at(index).get('lumSkuName')?.setValue('');
  }

  displayFn(rfq :any) :string {
    // Check if rfq type is string
    if (typeof rfq === 'string') {
      return rfq;
    }

    return `[${rfq?.sku}] [${rfq?.product_name}]`;
  }

  onLeadtimeDaysChange(leadtimeDays :any, index :number) :void {
    const productId = this.productsFormArray.at(index).get('id')?.value;
    const data = {
      leadtimeDays,
    };

    this.updateProduct({ data, index, productId });
  }

  onBufferQtyChange(bufferQuantity :any, index :number) :void {
    const productId = this.productsFormArray.at(index).get('id')?.value;
    const data = {
      bufferQuantity,
    };

    this.updateProduct({ data, index, productId });
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.queryParams = {
      ...this.queryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    };

    this.getProducts();
  }

  onFilterChange(value :string, key :string) :void {
    this.queryParams = {
      ...this.queryParams,
      [key]: value,
    };

    this.getProducts();
  }

  onSyncProducts() :void {
    this.loadingProducts = true;
    this.appIntegrationAccountProductsApiService
      .syncProducts({ appIntegrationAccountId: this.accountId.toString() })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingProducts = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const { data, meta } = response;
          this.products = data;
          this.pagination = meta;
          if (data.length) {
            this.initForm(data);
          }

          this.snackBar.open(
            'Sync in progress - this may take a while. Please refresh the page after a few minutes.',
            'Success',
            { duration: SnackBarConfig.DEFAULT.duration },
          );
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to sync products', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onAutoMapProducts() :void {
    this.autoMapLoading = true;

    this.appIntegrationAccountProductsApiService
      .autoMapProducts(this.accountId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.autoMapLoading = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.snackBar.open(response.success, 'Info', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to auto map products',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  onOpenLocationDialog() :void {
    this.dialog
      .open(AppIntegrationAccountLocationComponent, {
        data: {
          appIntegrationAccount: this.appIntegrationAccount || {},
        },
        width: DialogConfig.sizes.large,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {});
  }

  onExportProduct() :void {
    this.importerService
      .getExportTemplateData({
        type: ImportTypeConstant.APP_INTEGRATION_ACCOUNT_PRODUCTS,
        appIntegrationAccountId: this.accountId.toString(),
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response?.length) {
            this.importerService.exportToExcel(
              response,
              'App Integration Account Products',
            );
          } else {
            this.snackBar.open('No products found to export', 'Info', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to export products', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onApplyFilter() :void {
    const dialogRef = this.dialog.open(
      AppIntegrationAccountProductFilterFormComponent,
      {
        width: '800px',
        disableClose: true,
        data: {
          accountId: this.accountId,
          filters: this.queryParams,
        },
      },
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.queryParams = {
          ...this.queryParams,
          ...result,
        };
        this.getProducts();
      }
    });
  }

  onOpenBulkCreateProductsModal() :void {
    const dialogRef = this.dialog.open(
      AppIntegrationAccountBulkCreateProductsModalComponent,
      {
        width: '800px',
        disableClose: true,
        data: {
          appIntegrationAccountId: this.accountId,
        },
      },
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Refresh the products list to show the newly created products
        this.getProducts();
      }
    });
  }

  private getAppIntegrationAccountNoPullProducts() :void {
    this.appIntegrationAccountApiService
      .getIntegrationAppsFromActions({
        actions: 'pullProducts',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          const accountExists = response.some(
            (app) => app === this.appIntegrationAccount?.app,
          );
          this.isNotSupportedPullProducts = !accountExists;
        },
      });
  }

  private updateProduct(params :{
    data :any;
    index :number;
    productId :number;
  }) :void {
    const { data, productId, index } = params;
    this.appIntegrationAccountProductsApiService
      .update(productId, data)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.snackBar.open('Successfully updated product', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to update product', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initQueryParams() :void {
    this.queryParams = {
      limit: PaginationConfig.perPage.toString(),
      page: PaginationConfig.page.toString(),
      relations: 'lumSku',
      mapTo: EAppIntegrationAccountProductFilter.ALL,
      synchronous: 'false',
    };
  }

  private getProducts() :void {
    this.loadingProducts = true;
    this.appIntegrationAccountProductsApiService
      .get({
        ...this.queryParams,
        appIntegrationAccountId: this.accountId.toString(),
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingProducts = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const { data, meta } = response;
          this.products = data;
          this.pagination = meta;
          if (data.length) {
            this.initForm(data);
          }
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to get products', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private getAppIntegrationAccount() :void {
    this.appIntegrationAccountApiService
      .getById(this.accountId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (appIntegrationAccount :IAppIntegrationAccount) => {
          this.appIntegrationAccount =
            appIntegrationAccount || ({} as IAppIntegrationAccount);
          this.getAppIntegrationAccountNoPullProducts();
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to get app integration account',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private initForm(products :IAppIntegrationAccountProduct[]) :void {
    this.form = this.fb.group({
      products: this.fb.array(
        products.map((product) => {
          return this.fb.group({
            id: [product.id],
            lumSkuId: [product.lumSkuId],
            lumSkuName: [
              product.lumSku
                ? `[${product.lumSku?.internalSku}] ${product.lumSku?.productName}`
                : '',
            ],
            productId: [product.productId],
            appIntegrationAccountId: [product.appIntegrationAccountId],
            sku: [product.sku],
            productName: [product.productName],
            bufferQuantity: [product.bufferQuantity || 0],
            leadtimeDays: [product.leadtimeDays || 0],
            currentInventory: [product.currentInventory || 0],
            price: [product.price || 0],
            updatedAt: [product.updatedAt || null],
          });
        }),
      ),
    });
  }

  get productsFormArray() :UntypedFormArray {
    return this.form.get('products') as UntypedFormArray;
  }

  get showGetLocations() :boolean {
    return (
      this.appIntegrationAccount?.settings?.show_inventory_location === 'true' ||
      false
    );
  }
}
